import * as fs from 'node:fs';
import * as os from 'node:os';
import * as path from 'node:path';

import { Integration } from '../../lib/Constants';
import { spawn, execSync } from 'node:child_process';
import type { ChildProcess } from 'node:child_process';
import { dim, green, red } from '../../lib/Helper/Logging';
import { expect } from 'vitest';
import { PackageDotJson } from '../../src/utils/package-json';

export const KEYS = {
  UP: '\u001b[A',
  DOWN: '\u001b[B',
  LEFT: '\u001b[D',
  RIGHT: '\u001b[C',
  ENTER: '\r',
  SPACE: ' ',
};

export const TEST_ARGS = {
  AUTH_TOKEN: process.env.SENTRY_TEST_AUTH_TOKEN || 'TEST_AUTH_TOKEN',
  PROJECT_DSN:
    process.env.SENTRY_TEST_DSN || 'https://public@dsn.ingest.sentry.io/1337',
  ORG_SLUG: process.env.SENTRY_TEST_ORG || 'TEST_ORG_SLUG',
  PROJECT_SLUG: process.env.SENTRY_TEST_PROJECT || 'TEST_PROJECT_SLUG',
};

export const log = {
  success: (message: string) => {
    green(`[SUCCESS] ${message}`);
  },
  info: (message: string) => {
    dim(`[INFO] ${message}`);
  },
  error: (message: unknown) => {
    function formatMessage(message: unknown, depth: number): string {
      if (depth > 3) {
        return '...';
      }

      if (message instanceof Error) {
        return JSON.stringify(
          {
            name: message.name,
            message: message.message,
            stack: message.stack,
            ...(message.cause
              ? {
                  cause: formatMessage(message.cause, depth + 1),
                }
              : {}),
          },
          null,
          2,
        );
      }
      return String(message);
    }
    red(`[ERROR] ${formatMessage(message, 0)}`);
  },
};

/**
 * Creates an isolated test environment by copying a test application to a temporary directory.
 * Each call creates a NEW unique temporary directory, allowing multiple isolated environments
 * per test file (useful for tests that run the wizard multiple times with different configs).
 *
 * @param testAppName - Name of the test application folder (e.g., 'nextjs-16-test-app')
 * @returns Object with projectDir path and cleanup function
 */
export function createIsolatedTestEnv(testAppName: string): {
  projectDir: string;
  cleanup: () => void;
} {
  const sourceDir = path.resolve(
    __dirname,
    '../test-applications',
    testAppName,
  );
  const tmpBaseDir = path.join(os.tmpdir(), 'sentry-wizard-e2e');

  if (!fs.existsSync(tmpBaseDir)) {
    fs.mkdirSync(tmpBaseDir, { recursive: true });
  }

  const projectDir = fs.mkdtempSync(path.join(tmpBaseDir, `${testAppName}-`));

  log.info(`Created isolated test env at: ${projectDir}`);

  try {
    fs.cpSync(sourceDir, projectDir, { recursive: true });
  } catch (e) {
    log.error('Error copying test application');
    log.error(e);
    throw e;
  }

  initGit(projectDir);

  const cleanup = () => {
    try {
      const keepOnFailure = process.env.SENTRY_WIZARD_E2E_KEEP_TEMP === 'true';

      if (keepOnFailure) {
        log.info(`Keeping temp directory for debugging: ${projectDir}`);
      } else {
        fs.rmSync(projectDir, { recursive: true, force: true });
        log.info(`Cleaned up isolated test env: ${projectDir}`);
      }
    } catch (e) {
      log.error(`Error cleaning up test environment at ${projectDir}`);
      log.error(e);
    }
  };

  return { projectDir, cleanup };
}

export class ProcessRunner {
  taskHandle: ChildProcess;

  constructor(
    cmd: string,
    args: string[],
    opts?: {
      cwd?: string;
      debug?: boolean;
    },
  ) {
    this.taskHandle = spawn(cmd, args, { cwd: opts?.cwd, stdio: 'pipe' });

    if (opts?.debug) {
      this.taskHandle.stdout?.pipe(process.stdout);
      this.taskHandle.stderr?.pipe(process.stderr);
    }
  }

  /**
   * Waits for the task to exit with a given `statusCode`.
   *
   * @returns a promise that resolves to `true` if the run ends with the status
   * code, or it rejects when the `timeout` was reached.
   */
  waitForStatusCode(
    statusCode: number | null,
    options: {
      /** Timeout in ms */
      timeout?: number;
    } = {},
  ) {
    const { timeout } = {
      timeout: 60_000,
      ...options,
    };

    return new Promise<boolean>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.kill();
        reject(
          new Error(`Timeout waiting for status code: ${statusCode ?? 'null'}`),
        );
      }, timeout);

      this.taskHandle.on('error', (err: Error) => {
        clearTimeout(timeoutId);
        reject(err);
      });

      this.taskHandle.on('exit', (code: number | null) => {
        clearTimeout(timeoutId);
        resolve(code === statusCode);
      });
    });
  }

  /**
   * Waits for the provided output with `.includes()` logic.
   *
   * @returns a promise that resolves to `true` if the output was found, `false` if the output was not found within the
   * timeout and `optional: true` is set, or it rejects when the timeout was reached with `optional: false`
   */
  waitForOutput(
    output: string,
    options: {
      /** Timeout in ms */
      timeout?: number;
      /** Whether to always resolve after the timeout, no matter whether the input was actually found or not. */
      optional?: boolean;
    } = {},
  ) {
    const { timeout, optional } = {
      timeout: 60_000,
      optional: false,
      ...options,
    };

    return new Promise<boolean>((resolve, reject) => {
      let outputBuffer = '';
      const timeoutId = setTimeout(() => {
        this.taskHandle.off('error', errorListener);
        this.taskHandle.stdout?.off('data', dataListener);

        this.kill();
        if (optional) {
          // The output is not found but it's optional so we can resolve the promise with false
          resolve(false);
        } else {
          reject(
            new Error(
              `Timeout waiting for output: ${output}. Got the following instead: ${outputBuffer}`,
            ),
          );
        }
      }, timeout);

      const dataListener = (data: string) => {
        outputBuffer += data;
        if (outputBuffer.includes(output)) {
          clearTimeout(timeoutId);
          this.taskHandle.off('error', errorListener);
          this.taskHandle.stdout?.off('data', dataListener);
          // The output is found so we can resolve the promise with true
          resolve(true);
        }
      };

      const errorListener = (err: Error) => {
        this.taskHandle.off('error', errorListener);
        this.taskHandle.stdout?.off('data', dataListener);
        clearTimeout(timeoutId);
        reject(err);
      };

      this.taskHandle.on('error', errorListener);
      this.taskHandle.stdout?.on('data', dataListener);
    });
  }

  kill() {
    this.taskHandle.stdin?.destroy();
    this.taskHandle.stderr?.destroy();
    this.taskHandle.stdout?.destroy();
    this.taskHandle.kill('SIGINT');
    this.taskHandle.unref();
  }
}

export function getWizardCommand(integration: Integration): string {
  const binName = process.env.SENTRY_WIZARD_E2E_TEST_BIN
    ? ['dist-bin', `sentry-wizard-${process.platform}-${process.arch}`]
    : ['dist', 'bin.js'];
  const binPath = path.join(__dirname, '..', '..', ...binName);

  const args = [
    '--debug',
    '-i',
    integration,
    '--preSelectedProject.authToken',
    TEST_ARGS.AUTH_TOKEN,
    '--preSelectedProject.dsn',
    TEST_ARGS.PROJECT_DSN,
    '--preSelectedProject.orgSlug',
    TEST_ARGS.ORG_SLUG,
    '--preSelectedProject.projectSlug',
    TEST_ARGS.PROJECT_SLUG,
    '--disable-telemetry',
  ];

  return `${binPath} ${args.join(' ')}`;
}

/**
 * Create a file with the given content
 *
 * @param filePath
 * @param content
 */
export function createFile(filePath: string, content?: string) {
  return fs.writeFileSync(filePath, content || '');
}

/**
 * Modify the file with the new content
 *
 * @param filePath
 * @param oldContent
 * @param newContent
 */
export function modifyFile(
  filePath: string,
  replaceMap: Record<string, string>,
) {
  const fileContent = fs.readFileSync(filePath, 'utf-8');
  let newFileContent = fileContent;

  for (const [oldContent, newContent] of Object.entries(replaceMap)) {
    newFileContent = newFileContent.replace(oldContent, newContent);
  }

  fs.writeFileSync(filePath, newFileContent);
}

/**
 * Read the file contents and check if it does not contain the given content
 *
 * @param {string} filePath
 * @param {(string | string[])} content
 */
export function checkFileDoesNotContain(
  filePath: string,
  content: string | string[],
) {
  const fileContent = fs.readFileSync(filePath, 'utf-8');
  const contentArray = Array.isArray(content) ? content : [content];

  for (const c of contentArray) {
    expect(fileContent).not.toContain(c);
  }
}

/**
 * Read the file contents and check if it contains the given content
 *
 * @param {string} filePath
 * @param {(string | string[])} content
 */
export function checkFileContents(
  filePath: string,
  content: string | string[],
) {
  const fileContent = fs.readFileSync(filePath, 'utf-8');
  const contentArray = Array.isArray(content) ? content : [content];
  for (const c of contentArray) {
    expect(fileContent).toContain(c);
  }
}

/**
 * Check if the file exists
 *
 * @param filePath
 */
export function checkFileExists(filePath: string) {
  expect(fs.existsSync(filePath)).toBe(true);
}

/**
 * Check if the file does not exist
 *
 * @param filePath
 */
export function checkFileDoesNotExist(filePath: string) {
  expect(fs.existsSync(filePath)).toBe(false);
}

/**
 * Check if the package.json lists the given package as a dependency or dev dependency
 *
 * @param projectDir
 * @param integration
 */
export function checkPackageJson(
  projectDir: string,
  packageName: string,
  devDependency = false,
) {
  const packageJson = fs.readFileSync(`${projectDir}/package.json`, 'utf-8');
  const packageJsonObject = JSON.parse(packageJson) as PackageDotJson;

  const packageVersion =
    packageJsonObject.dependencies?.[packageName] ||
    (devDependency && packageJsonObject.devDependencies?.[packageName]);

  expect(packageVersion).toBeTruthy();
}

/**
 * Check if the .sentryclirc contains the auth token
 *
 * @param projectDir
 */
export function checkSentryCliRc(projectDir: string) {
  checkFileContents(
    `${projectDir}/.sentryclirc`,
    `token=${TEST_ARGS.AUTH_TOKEN}`,
  );
}

/**
 * Check if the .env.sentry-build-plugin contains the auth token
 * @param projectDir
 */
export function checkEnvBuildPlugin(projectDir: string) {
  checkFileContents(
    `${projectDir}/.env.sentry-build-plugin`,
    `SENTRY_AUTH_TOKEN=${TEST_ARGS.AUTH_TOKEN}`,
  );
}

/**
 * Check if the sentry.properties contains the auth token
 * @param projectDir
 */
export function checkSentryProperties(projectDir: string) {
  checkFileContents(
    `${projectDir}/sentry.properties`,
    `auth_token=${TEST_ARGS.AUTH_TOKEN}`,
  );
}

/**
 * Check if the project builds
 * Check if the project builds and ends with status code 0.
 * @param projectDir
 */
export async function checkIfBuilds(projectDir: string) {
  const npmRunner = new ProcessRunner('npm', ['run', 'build'], {
    cwd: projectDir,
  });

  const builtSuccessfully = await npmRunner.waitForStatusCode(0, {
    timeout: 120_000,
  });

  expect(builtSuccessfully).toBe(true);
}

/**
 * Check if the project lints successfully
 * Runs `npm run lint` and expects status code 0.
 * @param projectDir
 */
export async function checkIfLints(projectDir: string) {
  const npmRunner = new ProcessRunner('npm', ['run', 'lint'], {
    cwd: projectDir,
  });

  const lintedSuccessfully = await npmRunner.waitForStatusCode(0, {
    timeout: 120_000,
  });

  expect(lintedSuccessfully).toBe(true);
}

/**
 * Check if the flutter project builds
 * @param projectDir
 */
export async function checkIfFlutterBuilds(
  projectDir: string,
  expectedOutput: string,
  debug = false,
) {
  const flutterRunner = new ProcessRunner('flutter', ['build', 'web'], {
    cwd: projectDir,
    debug: debug,
  });

  const outputReceived = await flutterRunner.waitForOutput(expectedOutput, {
    timeout: 120_000,
  });

  expect(outputReceived).toBe(true);
}

/**
 * Check if the React Native project bundles successfully for the specified platform.
 * Returns a boolean indicating if the process exits with status code 0.
 * @param projectDir The root directory of the React Native project.
 * @param platform The platform to bundle for ('ios' or 'android').
 * @param debug runs the command in debug mode if true
 */
export async function checkIfReactNativeBundles(
  projectDir: string,
  platform: 'ios' | 'android',
  debug = false,
): Promise<boolean> {
  const entryFile = 'index.js';
  const dev = 'false'; // Test a production-like bundle
  let bundleOutput: string;
  let assetsDest: string;

  if (platform === 'ios') {
    bundleOutput = './ios/main.jsbundle';
    assetsDest = './ios';
  } else {
    // android
    bundleOutput = './android/app/src/main/assets/index.android.bundle';
    assetsDest = './android/app/src/main/res';
  }

  const bundleCommandArgs = [
    'react-native',
    'bundle',
    '--entry-file',
    entryFile,
    '--platform',
    platform,
    '--dev',
    dev,
    '--bundle-output',
    bundleOutput,
    '--assets-dest',
    assetsDest,
  ];

  const npxRunner = new ProcessRunner('npx', bundleCommandArgs, {
    cwd: projectDir,
    debug: debug,
  });

  const builtSuccessfully = await npxRunner.waitForStatusCode(0, {
    timeout: 300_000,
  });

  npxRunner.kill();

  return builtSuccessfully;
}

/**
 * Check if the Expo project exports successfully for the specified platform.
 * Returns a boolean indicating if the process exits with status code 0.
 * @param projectDir The root directory of the Expo project.
 * @param platform The platform to export for ('ios', 'android', or 'web').
 * @param debug runs the command in debug mode if true
 */
export async function checkIfExpoBundles(
  projectDir: string,
  platform: 'ios' | 'android' | 'web',
  debug = false,
): Promise<boolean> {
  const exportCommandArgs = ['expo', 'export', '--platform', platform];

  const npxRunner = new ProcessRunner('npx', exportCommandArgs, {
    cwd: projectDir,
    debug: debug,
  });

  const builtSuccessfully = await npxRunner.waitForStatusCode(0, {
    timeout: 300_000,
  });

  npxRunner.kill();
  return builtSuccessfully;
}

/**
 * Check if the project runs on dev mode
 * @param projectDir
 * @param expectedOutput
 */
export async function checkIfRunsOnDevMode(
  projectDir: string,
  expectedOutput: string,
) {
  const npmRunner = new ProcessRunner('npm', ['run', 'dev'], {
    cwd: projectDir,
  });

  expect(
    await npmRunner.waitForOutput(expectedOutput, {
      timeout: 120_000,
    }),
  ).toBe(true);

  npmRunner.kill();
}

/**
 * Check if the project runs on prod mode
 * @param projectDir
 * @param expectedOutput
 */
export async function checkIfRunsOnProdMode(
  projectDir: string,
  expectedOutput: string,
  startCommand = 'start',
) {
  const npmRunner = new ProcessRunner('npm', ['run', startCommand], {
    cwd: projectDir,
  });

  expect(
    await npmRunner.waitForOutput(expectedOutput, {
      timeout: 120_000,
    }),
  ).toBe(true);

  npmRunner.kill();
}

/**
 * Initialize a git repository in the given directory
 * @param projectDir
 */
function initGit(projectDir: string): void {
  try {
    execSync('git init', { cwd: projectDir });
    // Add all files to the git repo
    execSync('git add -A', { cwd: projectDir });
    // Add author info to avoid git commit error
    execSync('git config user.email test@test.sentry.io', { cwd: projectDir });
    execSync('git config user.name Test', { cwd: projectDir });
    execSync('git commit -m init', { cwd: projectDir });
  } catch (e) {
    log.error('Error initializing git');
    log.error(e);
  }
}
